// assets/multi-step-modal.js

if (!customElements.get('multi-step-modal')) {
  customElements.define(
    'multi-step-modal',
    class MultiStepModal extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.modalId = `#MultiStepModal-${this.sectionId}`;
        this.dataElement = document.querySelector(`#MultiStepData-${this.sectionId}`);

        // --- Find Modal Element ---
        this.modal = document.querySelector(this.modalId);

        if (!this.modal || !this.dataElement) {
          console.error('MultiStepModal: Could not find modal or data element. Aborting setup.');
          return;
        }

        // --- Parse Config ---
        try {
          this.config = JSON.parse(this.dataElement.textContent);
          if (typeof this.config.steps === 'string') { // Handle double encoding if necessary
             this.config.steps = JSON.parse(this.config.steps);
          }
          if (!this.config.steps || !Array.isArray(this.config.steps)) {
             throw new Error("Steps data is missing or not an array");
          }
          this.translations = this.config.translations || {};
        } catch (e) {
          console.error('MultiStepModal: Error parsing configuration data.', e);
          this.config = null;
          return; // Abort if config fails
        }
       this.childElementsFound = false;
       this.cart = null;
      }

      PRESCRIPTION_ADDON_RULES = [
        {
            visionType: 'single',
            parameter: 'SPH',
            condition: (val) => val > 5.75 || val < -5.75,
            variantId: '48642668200169'
        },
        {
            visionType: 'single',
            parameter: 'CYL',
            condition: (val) => val < -2.00,
            variantId: '47982947565801'
        },
        {
            visionType: 'bifocal',
            parameter: 'SPH',
            condition: (val) => val > 5.75 || val < -5.75,
            variantId: '48285100212457'
        },
        {
            visionType: 'bifocal',
            parameter: 'CYL',
            condition: (val) => typeof val === 'number' && !isNaN(val),
            variantId: '47901645242601'
        },
        {
            visionType: 'progressive',
            parameter: 'SPH',
            condition: (val) => val > 5.75 || val < -5.75,
            variantId: '47836120416489'
        },
        {
            visionType: 'progressive',
            parameter: 'CYL',
            condition: (val) => typeof val === 'number' && !isNaN(val),
            variantId: '47833366462697'
        }
      ];

      connectedCallback() {
        this.initializeButtonState();

        // Initial setup if needed, though most logic runs on modal open
        if (!this.cart) {
            this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
            if (!this.cart) {
              console.warn('MultiStepModal: Cart element (cart-notification or cart-drawer) not found on connect.');
            } 
        }
        
        if (!this.childElementsFound && this.modal && this.config) {
            this.overviewContainer = this.modal.querySelector('.multi-step-modal__overview-content');
            this.stepsContainer = this.modal.querySelector('.multi-step-modal__steps');
            this.stepTitleEl = this.modal.querySelector('.multi-step-modal__step-title');
            this.stepCounterEl = this.modal.querySelector('.multi-step-modal__step-counter');
            this.stepContentEl = this.modal.querySelector('.multi-step-modal__step-content');
            this.subtotalValueEl = this.modal.querySelector('.multi-step-modal__subtotal-value');
            this.navigationContainer = this.modal.querySelector('.multi-step-modal__navigation');

            // *** FIX: Select the correct loading spinner element ***
            this.loadingSpinner = this.modal.querySelector('.loading__spinner'); // Use the class from the snippet

            // *** FIX: Check the correct loading spinner variable ***
            const elementsFound = this.overviewContainer && this.stepsContainer && this.stepTitleEl &&
                                this.stepCounterEl && this.stepContentEl && this.subtotalValueEl &&
                                this.navigationContainer && this.loadingSpinner; // Check loadingSpinner here

            if (elementsFound) {
                this.modal.addEventListener('modal:open', this.onModalOpen.bind(this));
                this.modal.addEventListener('modal:close', this.onModalClose.bind(this));

                // --- Listener 1: Handle RADIO BUTTON changes ONLY ---
                this.stepContentEl.addEventListener('change', this.handleOptionChange.bind(this));

                // --- Listener 2: Remove focusin listener (no longer needed here) ---
                // this.stepContentEl.addEventListener('focusin', (event) => { ... });

                this.stepContentEl.addEventListener('change', (event) => {
                  const target = event.target;

                  // Case 1: A prescription dropdown was changed
                  if (target.matches('select.prescription-grid__select')) {
                    this.handlePrescriptionChange(target);
                  }
                  // Case 2: An add-on card was interacted with
                  else if (target.closest('.multi-step-addons__card-wrapper')) { // <<< CORRECTED SELECTOR
                    this.handleAddonChange(target);
                  }
                  // Case 3: A main selection radio button was changed
                  else if (target.matches('input[type="radio"]')) {
                    this.handleOptionChange(event);
                  }
                });

                // Add a separate BLUR listener ONLY for the prescription grid's 'is-active' bug
                this.stepContentEl.addEventListener('blur', (event) => {
                  if (event.target.matches('select.prescription-grid__select')) {
                    event.target.classList.add('is-active');
                    this.updateNavigationButtonsState();
                  }
                }, true);

                // --- Listener 4: Navigation clicks (Unchanged) ---
                this.navigationContainer.addEventListener('click', this.handleNavigationClick.bind(this));

                // --- Listener 5: Variant Selector for Add Ons ---
                this.stepContentEl.addEventListener('change', (event) => {
                  const target = event.target;
                  const card = target.closest('.multi-step-addons__card');
                  if (!card) return;

                  const productId = card.dataset.productId;
                  const isToggle = target.matches('.addon-toggle-checkbox');
                  const isVariantSelector = target.matches('.addon-variant-radio');

                  if (isVariantSelector) {
                    const selectedVariantId = target.value;
                    const variant = this.findVariantById(productId, selectedVariantId);
                    if (!variant) return;

                    // 1. Update card's state
                    card.dataset.selectedVariantId = selectedVariantId;

                    // 2. Update card's UI
                    const imageEl = card.querySelector('.multi-step-addons__card-media img');
                    const variantTitleEl = card.querySelector('.multi-step-addons__card-variant-title');
                    const priceContainer = card.querySelector('.multi-step-addons__card-price');

                    if (imageEl) imageEl.src = target.dataset.variantImage;
                    if (variantTitleEl) variantTitleEl.textContent = target.dataset.variantTitle;
                    if (priceContainer) this.renderAddonPrice(priceContainer, variant);
                  }

                  const mainToggle = card.querySelector('.addon-toggle-checkbox');
                  const currentVariantId = card.dataset.selectedVariantId;

                  // 3. Update additionalItems array based on toggle state
                  // First, remove any existing item for this product
                  this.additionalItems = this.additionalItems.filter(item => {
                    const variantForThisItem = this.findVariantById(productId, item.id);
                    return !variantForThisItem; // Keep if it doesn't belong to this product
                  });

                  // If the toggle is now checked, add the current variant back
                  if (mainToggle.checked) {
                    this.additionalItems.push({
                      id: parseInt(currentVariantId),
                      quantity: 1
                    });
                  }

                  // 4. Recalculate and update subtotal
                  this.recalculateSubtotal();
                  this.updateSubtotalDisplay();
                });

                this.stepContentEl.addEventListener('click', (event) => {
                    // --- STEP 1: Look for the entire card wrapper first ---
                    const cardWrapper = event.target.closest('.multi-step-addons__card-wrapper');

                    // If the click wasn't on or inside an addon card, do nothing
                    if (!cardWrapper) {
                      return;
                    }

                    // --- STEP 2: Check if the card is disabled BEFORE doing anything else ---
                    if (cardWrapper.classList.contains('is-disabled') || cardWrapper.disabled == true) {
                       console.log("Clicked a disabled card. Preventing action.");
                       return;
                    }

                    // --- STEP 3: Find the checkbox INSIDE the clicked card ---
                    // No matter where on the card the user clicked, we find its specific checkbox.
                    const checkbox = cardWrapper.querySelector('.addon-toggle-checkbox');
                    if (!checkbox) {
                      console.error("Clicked an addon card, but no .addon-toggle-checkbox found inside.", cardWrapper);
                      return;
                    }

                    // --- STEP 4: Toggle the checkbox and dispatch the event ---
                    // This handles the case where the user might have clicked the label or SVG directly,
                    // which would toggle the checkbox automatically. By setting it explicitly, we ensure
                    // a consistent state regardless of the click target.
                    checkbox.checked = !checkbox.checked;
                    checkbox.dispatchEvent(new Event('change', { bubbles: true }));

                    console.log(`Card Click: Checkbox ${checkbox.id} is now ${checkbox.checked ? 'checked' : 'unchecked'}`);
                });

                const toggleTooltip = (iconElement) => {
                    // Find the tooltip content - it's the next sibling within the wrapper
                    const contentElement = iconElement.nextElementSibling;

                    if (contentElement && contentElement.classList.contains('tooltip-content')) {
                        const isHidden = contentElement.hidden;
                        // Close any other open tooltips first
                        this.closeAllTooltips(contentElement);
                        // Toggle the current one
                        contentElement.hidden = !isHidden;
                        // Update aria-expanded based on the new state
                        iconElement.setAttribute('aria-expanded', !isHidden);
                    } else {
                        console.error("Tooltip content not found as next sibling for icon:", iconElement);
                    }
                };

                // Click listener for tooltip icons
                this.stepContentEl.addEventListener('click', (event) => {
                    const icon = event.target.closest('.tooltip-icon');
                    if (icon) {
                       // Check if the click was specifically on the icon, not just within the wrapper
                       if (event.target === icon || icon.contains(event.target)) {
                            toggleTooltip(icon);
                       }
                    }
                });

                // Keyboard listener for tooltip icons (Enter/Space)
                this.stepContentEl.addEventListener('keydown', (event) => {
                     const icon = event.target.closest('.tooltip-icon');
                     if (icon && (event.key === 'Enter' || event.key === ' ')) {
                         event.preventDefault(); // Prevent page scroll on space
                         toggleTooltip(icon);
                     } else if (event.key === 'Escape') { // Close on Escape
                         this.closeAllTooltips();
                     }
                 });

                document.addEventListener('click', (event) => {
                    if (!event.target || !this.stepContentEl) return;

                    const clickedInsideTooltip = event.target.closest('.tooltip-wrapper');

                    if (!clickedInsideTooltip) {
                       this.closeAllTooltips();
                    }
                }, true);

                this.childElementsFound = true;
            } else {
                 console.error('MultiStepModal: Required child elements not found. Listeners not attached.');
            }
        }
      }

      onModalOpen() {
        if (!this.config) {
            console.error('Cannot open modal, config not loaded.');
            return;
        }
        this.resetState(); // Reset selections, subtotal etc.
        this.currentStepIndex = 0; // Start at the first step

        this.renderInitialOverview();

        this.renderStep(this.currentStepIndex); // Render the first step content
        // Note: updateOverview() will be called by handleOptionChange when a selection is made in step 1
      }

      onModalClose() {
        this.clearErrorMessages();
        this.resetState();
      }

      resetState() {
         this.currentStepIndex = -1;
         this.selections = {}; // Store selections: { stepIndex: { data } }
         this.basePrice = this.config.productPrice;
         this.subtotal = this.basePrice;
         this.lineItemProperties = {}; // For custom fields like prescription
         this.additionalItems = []; // For product_ref items
      }

      shouldRenderStep(stepIndex) {
        if (stepIndex <= 0) return true; // Step 0 always renders

        const stepData = this.config.steps[stepIndex];

        // console.log(`%c=== Checking if Step ${stepIndex} should render ===`, 'color: orange; font-weight: bold;');
        // console.log('Step data:', stepData);

        // If the step has no filter conditions, it should always be shown.
        if (!stepData.filter_value_condition || stepData.filter_value_condition.length === 0) {
            // console.log('%cNo filter condition - rendering step', 'color: green;');
            return true;
        }

        // Use our new helper to find the last relevant filter_value from ALL previous steps.
        const latestFilterValue = this.findLatestFilterValue(stepIndex);

        // console.log('Filter condition:', stepData.filter_value_condition);
        // console.log('Found latest preceding filter_value:', latestFilterValue);

        // If we couldn't find any relevant filter value, this step can't be rendered.
        if (!latestFilterValue) {
            console.warn(`Step ${stepIndex}: No preceding selection with a filter_value was found.`);
            return false;
        }

        // The core logic: Does the list of conditions for this step include the value from our last selection?
        const shouldRender = stepData.filter_value_condition.includes(latestFilterValue);

        //console.log(`%cShould render: ${shouldRender}`, shouldRender ? 'color: green;' : 'color: red;');

        return shouldRender;
      }

      shouldRenderOption(optionData, previousSelectionFilterValue) {
        // If no parent_option field or it's empty, always show the option
        if (!optionData.parent_option || 
            !Array.isArray(optionData.parent_option) || 
            optionData.parent_option.length === 0) {
            return true;
        }

        // If no previous filter value, don't show options with parent requirements
        if (!previousSelectionFilterValue) {
            return false;
        }

        // Check if any parent option matches the previous selection's filter value (case-sensitive)
        return optionData.parent_option.includes(previousSelectionFilterValue);
      }

      renderStep(index) {
         if (!this.config || index < 0 || index >= this.config.steps.length) {
           console.error("Invalid step index or missing config in renderStep:", index);
           this.modal?.hide();
           return;
         }
         this.currentStepIndex = index;

         const stepData = this.config.steps[index];
         const currentVisibleStepNumber = this.getVisibleStepNumber(index);

         this.stepTitleEl.textContent = stepData.step_title || '';
         this.stepCounterEl.textContent = `${this.translations.step || 'Step'} ${currentVisibleStepNumber}`;

         // Clear previous content and render new
         this.stepContentEl.innerHTML = '';
         this.renderStepContent(stepData); // This might call restoreSelectionUI

         // Render Navigation Buttons (sets initial state based on isLastVisible, disabled state might be refined later)
         this.renderNavigation(stepData);

         // Update Subtotal Display
         this.updateSubtotalDisplay();

         // Update Overview
         this.updateOverview();

         // --- ADDED: Ensure final button state is correct after rendering/restoring ---
         this.updateNavigationButtonsState();
         // --- END ADDED ---

         // Trap focus
         setTimeout(() => {
             const containerToFocus = this.stepContentEl;
             const focusableElements = getFocusableElements(this.modal?.querySelector('.modal-dialog__content')); // Safety check modal
             const firstFocusable = focusableElements[0];
             const initialFocusTarget = containerToFocus && containerToFocus.hasChildNodes() ? containerToFocus : firstFocusable; // Focus container only if it has content

              if (this.modal) { // Ensure modal exists
                  const dialog = this.modal.querySelector('[role="dialog"]');
                  if (dialog) {
                      if (initialFocusTarget) {
                           trapFocus(dialog, initialFocusTarget);
                           // Blur initially focused prescription selects
                           const firstSelect = initialFocusTarget.matches && initialFocusTarget.matches('.prescription-grid__select')
                                ? initialFocusTarget
                                : initialFocusTarget.querySelector?.('.prescription-grid__select');
                           if (firstSelect && document.activeElement === firstSelect) {
                               firstSelect.blur();
                           }
                      } else {
                           trapFocus(dialog); // Trap focus on dialog itself if nothing else focusable
                      }
                  }
              }
         }, 100);
      }

      renderStepContent(stepData) {
        switch (stepData.step_type) {
            case 'selection':
            this.renderSelectionStep(stepData);
            break;
            case 'input':
            if (stepData.custom_fields_json) {
                this.renderInputStep(stepData);
            } else {
                console.warn("Input step type without custom_fields_json:", stepData);
                this.stepContentEl.innerHTML = `<p>Configuration error: Input step needs custom_fields_json.</p>`;
            }
            break;
            case 'add-ons':
              this.renderAddonsStep(stepData);
              break;
            default:
            this.stepContentEl.innerHTML = `<p>Unsupported step type: ${stepData.step_type}</p>`;
        }
        this.restoreSelectionUI(this.currentStepIndex);
      }

      renderInitialOverview() {
        if (!this.overviewContainer) return;

        // Base Product/Frame Info
        let initialHTML = `<div class="overview-item overview-item--frame">`; // Add specific class
        initialHTML += `<span class="overview-item__label">${this.translations.frame || 'Frame'}</span>`;
        initialHTML += `<div class="overview-item__details">`;
        initialHTML += `<span class="overview-item__value">${this.config.productTitle} <span style="text-transform: capitalize">(${this.config.currentVariantTitle || ''})</span></span>`;
        // initialHTML += `<span class="overview-item__price">${this.formatMoney(this.basePrice)}</span>`;
        initialHTML += `</div></div>`;

        // Add a container specifically for step selections that will be updated later
        initialHTML += `<div class="multi-step-modal__overview-selections"></div>`;

        this.overviewContainer.innerHTML = initialHTML; // Set the initial content
      }
      
      renderSelectionStep(stepData) {
        const fragment = document.createDocumentFragment();
        let hasGroups = false;

        // Get previous step's selection filter value for parent option filtering
        const previousFilterValue = this.findLatestFilterValue(this.currentStepIndex);


        // Check for and render groups
        for (let i = 1; i <= 3; i++) {
            const groupLabel = stepData[`group_${i}_label`];
            const groupOptions = stepData[`group_${i}_options`]?.map(opt => opt) || []; 
            const groupLinkLabel = stepData[`group_${i}_link_label`];
            const groupLinkUrl = stepData[`group_${i}_link`];

            if (groupLabel && groupOptions.length > 0) {
                const groupContainer = document.createElement('div');
                groupContainer.className = 'product-option-group';

                const groupHeader = document.createElement('p');
                groupHeader.className = 'product-option-group__title';
                groupHeader.textContent = groupLabel;

                groupContainer.appendChild(groupHeader);

                let groupHasOptions = false;

                groupOptions.forEach(option => {
                    // Filter by parent option
                    if (this.shouldRenderOption(option, previousFilterValue)) {
                        groupContainer.appendChild(this.createOptionElement(option, this.currentStepIndex));
                        groupHasOptions = true;
                    }
                });

                // Only add group if it has at least one visible option
                if (groupHasOptions) {
                    hasGroups = true;

                    if (groupLinkLabel && groupLinkUrl) {
                        const linkElement = document.createElement('a');
                        linkElement.href = groupLinkUrl;
                        linkElement.textContent = groupLinkLabel;
                        linkElement.className = 'product-option-group__link link link--accent';
                        linkElement.target = '_blank';
                        linkElement.rel = 'noopener noreferrer';
                        groupContainer.appendChild(linkElement);
                    }
                    
                    fragment.appendChild(groupContainer);
                }
            }
        }

        // Render ungrouped options
        const ungroupedOptions = stepData.options?.map(opt => opt) || [];
        if (ungroupedOptions.length > 0) {
            const ungroupedContainer = hasGroups ? document.createElement('div') : null;
            if (hasGroups) {
                ungroupedContainer.className = 'product-option-group product-option-group--ungrouped';
            }

            let hasUngroupedOptions = false;

            ungroupedOptions.forEach(option => {
                // Filter by parent option
                if (this.shouldRenderOption(option, previousFilterValue)) {
                    const optionElement = this.createOptionElement(option, this.currentStepIndex);
                    if (hasGroups) {
                        ungroupedContainer.appendChild(optionElement);
                    } else {
                        fragment.appendChild(optionElement);
                    }
                    hasUngroupedOptions = true;
                }
            });

            // Only add ungrouped container if it has options and we're using one
            if (hasGroups && hasUngroupedOptions) {
                fragment.appendChild(ungroupedContainer);
            }
        }

        // Check if any options were rendered at all
        if (!fragment.hasChildNodes()) {
            const noOptionsMsg = document.createElement('p');
            noOptionsMsg.className = 'form__message';
            noOptionsMsg.textContent = 'No options available for your previous selection.';
            fragment.appendChild(noOptionsMsg);
            
            // Disable next button if no options
            const nextButton = this.navigationContainer?.querySelector('[data-action="next"], [data-action="add"]');
            if (nextButton) nextButton.disabled = true;
        }

        this.stepContentEl.appendChild(fragment);
      }

      createOptionElement(optionData, stepIndex) {
         const optionId = `step-${stepIndex}-option-${optionData.id || Math.random().toString(36).substr(2, 9)}`;
         const optionWrapper = document.createElement('div');
         optionWrapper.className = 'pdp-modal-product-option';
    
         const input = document.createElement('input');
         input.type = 'radio';
         input.name = `step-${stepIndex}-selection`;
         input.id = optionId;
         input.value = optionData.id || optionData.option_label;
         input.dataset.optionData = JSON.stringify(optionData);
         if (optionData.is_default && !this.selections[stepIndex]) {
             input.checked = true;
             // Default selection is handled later or via initial state setup if needed
         }
    
         const label = document.createElement('label');
         label.htmlFor = optionId;
         const shape = this.config?.swatchShape || 'circle';
         label.className = `swatch-input__label product-option__label${shape === 'square' ? ' swatch-input__label--square' : ''}`;
         label.title = optionData.option_label;

         const colorData = optionData.swatch_color_ref; // Resolved Color object reference
         let swatchElement = null;
         let isSwatchUnavailable = true; // Assume unavailable initially
         let styleString = '';

         if (colorData) {
             let isSwatchUnavailable = true;
             let backgroundStyle = '';

             // PRIORITIZE swatch_image_url from the JSON data
             if (colorData.swatch_image_url) {
                 // Assuming transformImageUrl handles sizing etc. if needed
                 const imageUrl = this.transformImageUrl(colorData.swatch_image_url, '50x'); // Example size
                 backgroundStyle = `background-image: url('${imageUrl}'); background-size: cover; background-position: ${colorData.focal_point || 'center'};`;
                 isSwatchUnavailable = false;
             }
             // FALLBACK to hex value
             else if (colorData.value) {
                 backgroundStyle = `background-color: ${colorData.value};`;
                 isSwatchUnavailable = false;
             }

             if (!isSwatchUnavailable) {
                 swatchElement = document.createElement('span');
                 // Add shape class to the swatch span itself
                 swatchElement.className = `swatch ${shape === 'square' ? 'swatch--square' : ''}`;
                 if (backgroundStyle) {
                      // Apply either background-image or background-color
                      styleString = backgroundStyle;
                      swatchElement.setAttribute('style', styleString.trim());
                 }
             }
         }

         if (!isSwatchUnavailable) {
           swatchElement = document.createElement('span');
           swatchElement.className = `swatch${shape === 'square' ? ' swatch--square' : ''}`;
           if (styleString) {
               swatchElement.setAttribute('style', styleString.trim());
           }
         }
        
         // *** Create wrapper for label text and price (Requirement 4) ***
         const labelContentWrapper = document.createElement('div');
         labelContentWrapper.className = 'product-option__label-content';
    
         const labelTextSpan = document.createElement('span');
         labelTextSpan.className = 'product-option__text';
         labelTextSpan.textContent = optionData.option_label;
         labelContentWrapper.appendChild(labelTextSpan); // Add text span
    
         const price = optionData.price_override ?? optionData.product_ref?.price;
         // *** Display price only if > 0 (Requirement 4 & 8) ***
         if (price !== undefined && price !== null && price > 0) {
            const priceDisplaySpan = document.createElement('span');
            priceDisplaySpan.className = 'product-option__price';
            // Add '+' manually, then the formatted price (handles Requirement 8 & 10)
            priceDisplaySpan.textContent = `+${this.formatMoney(price)}`;
            labelContentWrapper.appendChild(priceDisplaySpan); // Add price span
         } else if (price === 0 && (optionData.price_override !== null || optionData.product_ref?.id)) {
            // Handle explicit free / included option
            const priceDisplaySpan = document.createElement('span');
            priceDisplaySpan.className = 'product-option__price product-option__price--free';
            priceDisplaySpan.textContent = this.translations.included || '(Included)'; // Use translation
            labelContentWrapper.appendChild(priceDisplaySpan);
         }
         // *** End wrapper creation ***
    
         // Append elements to the label
         if (swatchElement) { // Append the swatch span first (only if created)
             label.appendChild(swatchElement);
         }
         label.appendChild(labelContentWrapper); // Append the wrapper containing text and price

         // Add hidden help text like swatch-input snippet (for accessibility)
         const helpTextSpan = document.createElement('span');
         helpTextSpan.className = 'visually-hidden';
         helpTextSpan.textContent = optionData.option_label; // Simple version
         label.appendChild(helpTextSpan);
         // You could add the 'unavailable' label here too if needed, based on product availability logic


         // Description
         if (optionData.description) {
             const description = document.createElement('p');
             description.className = 'product-option__description';
             description.innerHTML = optionData.description; // Assuming description might contain simple HTML
             // Decide where to append description relative to swatch/text
             label.appendChild(description); // Appending after text+swatch
         }

         optionWrapper.appendChild(input);
         optionWrapper.appendChild(label);

         return optionWrapper;
      }

      findLatestFilterValue(upToStepIndex) {
        // Start from the step right before the target one and go to the beginning
        for (let i = upToStepIndex - 1; i >= 0; i--) {
          const selection = this.selections[i];

          // Check if a valid, user-made selection with a filter_value exists for this step
          if (selection && !selection.skipped && selection.filter_value) {
            // We found it. This is the value we need to use for our checks.
            return selection.filter_value;
          }
        }
        // If the loop completes, no relevant filter_value was found in any previous steps.
        return null;
      }

      getPreviousSelectionLabels() {
        // console.log('%c[Debug] Running getPreviousSelectionLabels...', 'color: cyan;');
        const selectionLabels = new Set();
        // Loop through all selections made in steps before the current one
        for (let i = 0; i < this.currentStepIndex; i++) {
            const selection = this.selections[i];
            // If a valid selection with an option_label exists...
            if (selection && !selection.skipped && selection.option_label) {
                // Log the raw label before converting to lowercase
                // console.log(`[Debug] Found label in Step ${i}: "${selection.option_label}". Adding "${selection.option_label.toLowerCase()}" to set.`);
                selectionLabels.add(selection.option_label.toLowerCase());
            }
        }
        // console.log('%c[Debug] Final label set:', 'color: cyan; font-weight: bold;', selectionLabels);
        return selectionLabels;
      }

      // Helper function to add image transform parameters (basic version)
      // You might want a more robust version depending on CDN specifics
      transformImageUrl(url, size) {
        if (!url || typeof url !== 'string') return null; // Return null if no valid URL

        if (url.startsWith('//')) {
           return window.location.protocol + url;
        }

        return url;
       }

       getVisibleStepNumber(targetIndex) {
        let visibleCount = 0;
        // Loop from the beginning up to and including the current step
        for (let i = 0; i <= targetIndex; i++) {
            const stepData = this.config.steps[i];

            // Check if this step should have rendered based on its preceding selection
            if (!this.shouldRenderStep(i)) {
                continue; // This step would have been skipped, so don't count it.
            }

            // Check the special case for the prescription step
            const isPrescriptionInputStep =
                stepData.step_type === 'input' &&
                stepData.step_title?.toLowerCase().includes('prescription');

            if (isPrescriptionInputStep && this.shouldSkipPrescriptionStep(i)) {
                continue; // This step also would have been skipped.
            }

            // If we get here, the step is visible, so we increment our counter.
            visibleCount++;
        }
        return visibleCount;
      }

       // Helper to get price of selected option from previous step if needed for relative pricing
       getPreviousStepOptionPrice(currentStepIndex) {
           if (currentStepIndex > 0 && this.selections[currentStepIndex - 1]) {
               const prevSelectionData = this.selections[currentStepIndex - 1];
               return prevSelectionData.price_override ?? prevSelectionData.product_ref?.price ?? 0;
           }
           return 0;
       }

      renderInputStep(stepData) {
        const stepContentFragment = document.createDocumentFragment();
        const gridContainer = document.createElement('div');
        gridContainer.className = 'prescription-grid custom-fields-container';

        // --- 1. Get all previous filter selections (now case-insensitive) ---
        const previousLabels = this.getPreviousSelectionLabels();
        // Check if the set CONTAINS the label text. Use .includes() for partial matches if needed.
        const hasSingle = previousLabels.has('single'); // Assumes label is exactly "Single"
        const hasBifocal = previousLabels.has('bifocal'); // Assumes label is exactly "Bifocal"
        const hasProgressive = previousLabels.has('progressive'); // Assumes label is exactly "Progressive"
        const hasUltraSlim = previousLabels.has('ultra slim'); // Assumes label is exactly "Ultra Slim"

        // console.log('%c[Debug] Filter conditions checked:', 'color: orange;', { hasSingle, hasBifocal, hasProgressive, hasUltraSlim });

        // --- 2. Conditionally hide the ADD column ---
        if (hasSingle) {
            console.log('%c[Debug] "single" detected. Hiding ADD column.', 'color: orange;');
            gridContainer.classList.add('prescription-grid--hide-add');
        }

        try {
            const config = typeof stepData.custom_fields_json === 'string'
                ? JSON.parse(stepData.custom_fields_json)
                : stepData.custom_fields_json;

            if (!config || !config.columns || !config.rows || !config.dropdown_options) {
                throw new Error("Invalid custom_fields_json structure");
            }

            const rowOrder = ['OS', 'OD'];
            const headers = config.columns;
            const rowsData = config.rows;
            const dropdownOptions = config.dropdown_options;

            // --- THIS PART IS NOW RESTORED ---
            // Eye Headers loop (e.g., OS, OD)
            rowOrder.forEach((eyeKey) => {
                const eyeLabelData = rowsData.find(r => r.label?.includes(eyeKey));
                if (eyeLabelData) {
                    const eyeHeaderDiv = document.createElement('div');
                    eyeHeaderDiv.className = `prescription-grid__eye-header prescription-grid__eye-header--${eyeKey}`;
                    eyeHeaderDiv.style.gridArea = `eye-${eyeKey}`;
                    eyeHeaderDiv.textContent = eyeLabelData.label;
                    gridContainer.appendChild(eyeHeaderDiv);
                }
            });

            // Param Headers loop (e.g., SPH, CYL)
            headers.forEach((headerData) => {
                  const paramHeaderDiv = document.createElement('div');
                  paramHeaderDiv.className = 'prescription-grid__param-header';
                  paramHeaderDiv.textContent = headerData.header;
                  paramHeaderDiv.dataset.col = headerData.header; // Add data-col for CSS

                  if (headerData.tooltip) {
                    const tooltipWrapper = document.createElement('span');
                      tooltipWrapper.className = 'tooltip-wrapper';
                      const tooltipIcon = document.createElement('span');
                      tooltipIcon.className = 'tooltip-icon';
                      tooltipIcon.setAttribute('role', 'button');
                      tooltipIcon.setAttribute('tabindex', '0');
                      tooltipIcon.setAttribute('aria-label', `Info for ${headerData.header}`);
                      tooltipIcon.setAttribute('aria-expanded', 'false');
                      tooltipIcon.innerHTML = 'ⓘ';
                      tooltipWrapper.appendChild(tooltipIcon);
                      const tooltipContent = document.createElement('span');
                      tooltipContent.className = 'tooltip-content';
                      tooltipContent.textContent = headerData.tooltip;
                      tooltipContent.setAttribute('role', 'tooltip');
                      tooltipContent.hidden = true;
                      tooltipWrapper.appendChild(tooltipContent);
                      paramHeaderDiv.appendChild(tooltipWrapper);
                  }

                  const sanitizedGridArea = `param-${headerData.header.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
                  paramHeaderDiv.style.gridArea = sanitizedGridArea;
                  gridContainer.appendChild(paramHeaderDiv);
            });
            // --- END OF RESTORED SECTION ---


            // --- Render Select Inputs with Conditional Filtering ---
            rowOrder.forEach((eyeKey) => {
                headers.forEach((headerData) => {
                     const isSphField = headerData.header === 'SPH';
                     const isCylField = headerData.header === 'CYL';
                     const select = document.createElement('select');
                     const fieldName = `${headerData.header}_${eyeKey}`;
                     select.name = fieldName;
                     select.dataset.row = eyeKey;
                     select.dataset.col = headerData.header; // Add data-col for CSS
                     select.className = 'prescription-grid__select';

                     const options = dropdownOptions[headerData.header];
                     if (!Array.isArray(options)) return;

                     options.forEach(optValue => {
                         const numericValue = parseFloat(optValue);
                         let shouldSkip = false;

                        //  console.log(`--- Checking option value: "${optValue}" (Parsed as: ${numericValue}) ---`);

                         // SPH Filtering Logic
                         if (isSphField && hasUltraSlim && (hasSingle || hasBifocal || hasProgressive)) {
                            //  console.log('[Debug] SPH filtering is ACTIVE for this option.');
                             if (numericValue >= -5.00 && numericValue <= 5.00) {
                                //  console.log(`%c[Debug] Decision: SKIP. ${numericValue} is outside +/- 5.25`, 'color: red;');
                                 shouldSkip = true;
                             }
                         }

                         // CYL Filtering Logic
                         if (isCylField && hasUltraSlim && (hasSingle || hasBifocal || hasProgressive)) {
                            //  console.log('[Debug] CYL filtering is ACTIVE for this option.');
                             if (numericValue >= -4.00 && numericValue <= 4.00) {
                                //  console.log(`%c[Debug] Decision: SKIP. ${numericValue} is outside +/- 4.25`, 'color: red;');
                                 shouldSkip = true;
                             }
                         }

                         if (!shouldSkip) {
                            //  console.log(`%c[Debug] Decision: KEEP option.`, 'color: green;');
                             const option = document.createElement('option');
                             option.value = optValue;
                             option.textContent = optValue;
                             select.appendChild(option);
                         }
                     });

                     console.groupEnd();

                     const sanitizedHeader = headerData.header.replace(/[^a-zA-Z0-9_-]/g, '-');
                     select.style.gridArea = `select-${eyeKey}-${sanitizedHeader}`;
                     gridContainer.appendChild(select);
                 });
             });

            stepContentFragment.appendChild(gridContainer);

            const customHeader = stepData.custom_field_header;
            const customDescription = stepData.custom_field_description;
            const customLinkLabel = stepData.custom_field_link_label;
            const customLinkUrl = stepData.custom_field_link;

            // Only render if there's a URL and some text content (label is mandatory now)
            if (customLinkUrl && customLinkUrl !== "#" && customLinkLabel) {
                // 1. Create main wrapper div
                const customGroupWrapper = document.createElement('div');
                customGroupWrapper.className = 'product-option-group prescription-custom-content';

                // 2. Add Header if it exists (like group title)
                if (customHeader) {
                    const headerEl = document.createElement('h4'); // Use h4 or adjust heading level
                    headerEl.className = 'product-option-group__title'; // Reuse title class
                    headerEl.textContent = customHeader;
                    customGroupWrapper.appendChild(headerEl); // Add header to main wrapper
                }

                // 3. Create the Link element (like product-option)
                const linkElement = document.createElement('a');
                linkElement.href = customLinkUrl;
                // Replicate structure, using 'product-option' for spacing/layout
                linkElement.className = 'pdp-modal-product-option prescription-custom-link link--unstyled';
                linkElement.target = '_blank';
                linkElement.rel = 'noopener noreferrer';

                // 4. Create the Label element inside the link
                const labelElement = document.createElement('label');
                // Replicate label classes, potentially add unique class if needed
                labelElement.className = 'product-option__label';
                // Set title attribute for hover text (optional)
                labelElement.title = customLinkLabel;

                // 5. Create the Label Content wrapper
                const labelContentWrapper = document.createElement('div');
                labelContentWrapper.className = 'product-option__label-content';

                // 6. Add Label Text (Always visible)
                const labelTextSpan = document.createElement('span');
                labelTextSpan.className = 'product-option__text';
                labelTextSpan.textContent = customLinkLabel;
                labelContentWrapper.appendChild(labelTextSpan);

                // 7. Add FREE price display (Always visible)
                const priceDisplaySpan = document.createElement('span');
                priceDisplaySpan.className = 'product-option__price product-option__price--free';
                priceDisplaySpan.textContent = this.translations.included || 'FREE'; // Use translation
                labelContentWrapper.appendChild(priceDisplaySpan);

                // Append label content (text + price) to the label
                labelElement.appendChild(labelContentWrapper);

                // 8. Add Description if it exists (inside label, after content wrapper)
                if (customDescription) {
                    const descriptionEl = document.createElement('p');
                    descriptionEl.className = 'product-option__description'; // Reuse description class
                    descriptionEl.innerHTML = customDescription; // Allow basic HTML
                    labelElement.appendChild(descriptionEl); // Append description to label
                }

                // Append the complete label structure to the link element
                linkElement.appendChild(labelElement);

                // Append the link element to the main wrapper
                customGroupWrapper.appendChild(linkElement);

                // Append the whole group wrapper to the fragment
                stepContentFragment.appendChild(customGroupWrapper);

            } 

            // --- Group 1 Options Rendering ---
            if (stepData.group_1_label && stepData.group_1_options && Array.isArray(stepData.group_1_options) && stepData.group_1_options.length > 0) {
                const additionalOptionsContainer = document.createElement('div');
                additionalOptionsContainer.className = 'product-option-group prescription-extras';
                const groupHeader = document.createElement('h3');
                groupHeader.className = 'product-option-group__title';
                groupHeader.textContent = stepData.group_1_label;
                additionalOptionsContainer.appendChild(groupHeader);

                stepData.group_1_options.forEach((option, optIndex) => {
                    // --- Add try/catch around createOptionElement specifically ---
                    try {
                       const optionElement = this.createOptionElement(option, this.currentStepIndex);
                       additionalOptionsContainer.appendChild(optionElement);
                    } catch (optionError) {
                         console.error(`renderInputStep: Error creating Group 1 option element at index ${optIndex}:`, optionError, "Option data:", option);
                         // Optionally append an error message for this specific option
                    }
                });
                stepContentFragment.appendChild(additionalOptionsContainer);
            } 
        } catch (e) {
            // Log the specific error caught
            console.error("Error rendering input step:", e.message, e.stack); // Log stack trace too
            const errorPara = document.createElement('p');
            errorPara.className = 'multi-step-modal__error form__message form__message--error';
            // Display the caught error message for better debugging, or keep the generic one
            // errorPara.textContent = `Error: ${e.message}`;
            errorPara.textContent = "Error rendering prescription input. Please check configuration."; // Keep generic for user
            stepContentFragment.appendChild(errorPara);
        }

        this.stepContentEl.innerHTML = '';
        this.stepContentEl.appendChild(stepContentFragment);
      }


      renderNavigation(stepData) {
         this.navigationContainer.innerHTML = ''; // Clear previous buttons

         // Previous Button (No change here)
         if (this.currentStepIndex > 0) {
           const prevButton = document.createElement('button');
           prevButton.type = 'button';
           prevButton.className = 'button multi-step-modal__button--prev multi-step-modal__button--icon-prev link link--text focus-inset';
           prevButton.dataset.action = 'prev';
           prevButton.setAttribute('aria-label', this.translations.previous || 'Previous');
           if (this.translations.iconArrowSvg) {
               prevButton.innerHTML = this.translations.iconArrowSvg;
           } else {
               prevButton.textContent = this.translations.previous || 'Previous';
           }
           this.navigationContainer.appendChild(prevButton);
         }

         // Skip Button (No change here)
         if (stepData.optional) {
             const skipButton = document.createElement('button');
             skipButton.type = 'button';
             skipButton.className = 'button button--secondary multi-step-modal__button--skip';
             skipButton.textContent = this.translations.skipStep || 'Skip this step';
             skipButton.dataset.action = 'skip';
             this.navigationContainer.appendChild(skipButton);
         }

         // --- REFACTORED: Next / Add to Cart Button ---
         const mainActionButton = document.createElement('button');
         mainActionButton.type = 'button';
         mainActionButton.className = 'button button--primary multi-step-modal__button--next';
         mainActionButton.dataset.action = 'next'; // The action is now always 'next'.

         // Set button text based on a simple check. The real logic is in the click handler.
         const isLastStepInArray = this.currentStepIndex >= this.config.steps.length - 1;
         mainActionButton.textContent = isLastStepInArray ? (this.translations.addToCart || 'Add to Cart') : (this.translations.next || 'Next');

         // Disable button based on current step's selection state
         const isRequired = !stepData.optional;
         const hasSelection = !!this.selections[this.currentStepIndex] && !this.selections[this.currentStepIndex].skipped;
         mainActionButton.disabled = isRequired && !hasSelection;

         this.navigationContainer.appendChild(mainActionButton);
      }


      handleOptionChange(event) {
        const input = event.target.closest('input[type="radio"]');

        if (!input || !input.dataset.optionData) {
            return;
        }

        const stepIndex = this.currentStepIndex;
        let selectionData = {}, requiresRecalculation = false, requiresOverviewUpdate = false;
        const prescriptionStepIndex = this.findInputStepIndex();

        try { selectionData = JSON.parse(input.dataset.optionData); } catch(e) { console.error("Radio parse error:", e); return; }
        requiresRecalculation = true; 
        requiresOverviewUpdate = true;
        const wasNonPrescriptionSelected = this.shouldSkipPrescriptionStep(prescriptionStepIndex);
        const isNonPrescription = selectionData.option_label?.toLowerCase().includes('non-prescription');

        // Clear subsequent steps if needed
        if (prescriptionStepIndex > stepIndex && (isNonPrescription || wasNonPrescriptionSelected)) { 
            this.clearSubsequentSelections(stepIndex); 
        }
        else if (stepIndex !== prescriptionStepIndex) { 
            this.clearSubsequentSelections(stepIndex); 
        }

        // Clear prescription properties ONLY if change is before prescription step
        if (stepIndex < prescriptionStepIndex) { 
            this.clearPrescriptionProperties(); 
        }

        this.selections[stepIndex] = selectionData;

        if (requiresRecalculation) { 
            this.recalculateSubtotal(); 
            this.updateSubtotalDisplay(); 
        }
        if (requiresOverviewUpdate) { 
            this.updateOverview(); 
        }
        this.updateNavigationButtonsState();
      }

      updateNavigationButtonsState() {
        const nextButton = this.navigationContainer?.querySelector('[data-action="next"]');
        const addButton = this.navigationContainer?.querySelector('[data-action="add"]');
        const buttonToUpdate = nextButton || addButton;

        if (buttonToUpdate && this.config && this.config.steps[this.currentStepIndex]) {
            const stepData = this.config.steps[this.currentStepIndex];
            const isRequired = !stepData.optional;
            let isComplete = false;

            if (stepData.step_type === 'input') {
                isComplete = this.checkPrescriptionCompletion();
            } else if (stepData.step_type === 'selection') {
                isComplete = !!this.selections[this.currentStepIndex] && !this.selections[this.currentStepIndex].skipped;
            } else if (stepData.step_type === 'add-ons') {
                isComplete = true; // The add-ons step is always considered "complete"
            } else {
                isComplete = !isRequired;
            }

            buttonToUpdate.disabled = isRequired && !isComplete;
        }
      }

      shouldSkipPrescriptionStep(targetStepIndex) {
        // Ensure targetStepIndex is valid and selections exist
        if (targetStepIndex < 0 || !this.selections) return false;

        for (let i = 0; i < targetStepIndex; i++) {
            const selection = this.selections[i];
            // Check if a selection exists for this step, it wasn't skipped, and has an option_label
            if (selection && !selection.skipped && selection.option_label) {
                // Case-insensitive check
                if (selection.option_label.toLowerCase().includes('non-prescription')) {
                    return true; // Found "Non-prescription", should skip
                }
            }
        }
        return false; // No "Non-prescription" found in previous steps' state
      }

      handleNavigationClick(event) {
        const button = event.target.closest('button');
        if (!button || !button.dataset.action) return;

        const action = button.dataset.action;
        this.clearErrorMessages();

        switch (action) {
            case 'prev':
                if (this.currentStepIndex > 0) {
                    let prevIndex = this.currentStepIndex - 1;
                    // Keep going back if the previous step was auto-skipped
                    while (prevIndex > 0 && this.selections[prevIndex]?.skipped === true && this.selections[prevIndex]?.option_label === 'Skipped (Auto)') {
                            prevIndex--;
                    }
                    this.renderStep(prevIndex);
                }
                break;

            case 'skip':
                // Mark current step as skipped by user and update UI
                if (!this.selections[this.currentStepIndex]) {
                    this.selections[this.currentStepIndex] = { skipped: true, option_label: this.translations.skipped || 'Skipped' };
                } else {
                    this.selections[this.currentStepIndex].skipped = true;
                }
                this.clearSubsequentSelections(this.currentStepIndex);
                this.updateOverview();
                // INTENTIONAL FALL-THROUGH: After skipping, proceed to the 'next' logic.

            case 'next':
                // This is now the main logic hub.
                // It's only called AFTER a user action, so the state is correct.
                const nextIndex = this.getNextStepIndexToShow(this.currentStepIndex);

                if (nextIndex >= this.config.steps.length) {
                    // If the next step is out of bounds, we've reached the end. Add to cart.
                    this.handleAddToCart();
                } else {
                    // Otherwise, render the next valid step.
                    this.renderStep(nextIndex);
                }
                break;

            case 'add':
                // This case is now redundant but kept as a fallback.
                this.handleAddToCart();
                break;
        }
      }

      recalculateSubtotal() {
        let currentSubtotal = this.basePrice;

        // 1. Add price from standard SELECTION steps
        Object.values(this.selections).forEach(selection => {
            if (selection && !selection.skipped && selection.type !== 'input') {
                currentSubtotal += selection.price_override ?? selection.product_ref?.price ?? 0;
            }
        });

        // 2. Add price from checked ADD-ONS UI
        if (this.stepContentEl) {
            const checkedAddonCards = this.stepContentEl.querySelectorAll('.multi-step-addons__card-wrapper .addon-toggle-checkbox:checked');
            checkedAddonCards.forEach(toggle => {
                const cardWrapper = toggle.closest('.multi-step-addons__card-wrapper');
                const productId = parseInt(cardWrapper.dataset.productId);
                const productData = this.findProductById(productId);
                if (!productData) return;

                const currentlySelectedOptions = Array.from(cardWrapper.querySelectorAll('input.variant-radio-input:checked')).map(radio => radio.value);
                const variantToAdd = productData.variants.find(variant =>
                    currentlySelectedOptions.every((opt, index) => variant.options[index] === opt)
                );
                if (variantToAdd) currentSubtotal += variantToAdd.price;
            });
        }
        
        // 3. Add price from PRESCRIPTION addons
        const matchingPrescriptionAddons = this.getMatchingAddons();
        matchingPrescriptionAddons.forEach(rule => {
            // NEW: Check if data exists in map, then access .price
            const addonData = this.config.addonDataMap[rule.variantId];
            if (addonData) {
                currentSubtotal += addonData.price;
            }
        });

        this.subtotal = currentSubtotal;
      }

      updateSubtotalDisplay() {
        if (this.subtotalValueEl) {
          // Use formatMoney to respect decimal setting (Req 10)
          console.log(`%c[UI Update] Updating subtotal display to: ${this.formatMoney(this.subtotal)}`, 'color: orange;');
          this.subtotalValueEl.textContent = this.formatMoney(this.subtotal);
        }
      }

      updateOverview() {
        const selectionsContainer = this.overviewContainer?.querySelector('.multi-step-modal__overview-selections');
        if (!selectionsContainer) return;

        console.log('%c[UI Update] Updating overview panel...', 'color: orange;');
        let selectionsHTML = '';
        
        // --- PART 1: Render standard selection steps ---
        Object.values(this.selections).forEach(selection => {
          if (!selection || selection.skipped || selection.type === 'input') return;

          const stepConfig = this.config.steps.find(step => step.options && step.options.some(opt => opt.option_label === selection.option_label));
          if (!stepConfig) return;

          selectionsHTML += `<div class="overview-item">`;
          selectionsHTML += `<span class="overview-item__label">${stepConfig.summary_label || stepConfig.step_title}</span>`;
          selectionsHTML += `<div class="overview-item__details">`;
          if (selection.option_label == "Maybe next time") {
            selectionsHTML += `<span class="overview-item__value">None</span>`;
          } else {
            selectionsHTML += `<span class="overview-item__value">${selection.option_label || ''}</span>`;
          }
        //   const price = selection.price_override ?? selection.product_ref?.price ?? 0;
        //   if (price > 0) {
        //     selectionsHTML += `<span class="overview-item__price">${this.formatMoney(price)}</span>`;
        //   } else if (price === 0 && (selection.price_override !== null || selection.product_ref?.id)) {
        //     selectionsHTML += `<span class="overview-item__price overview-item__price--free">${this.translations.included || 'FREE'}</span>`;
        //   }
          selectionsHTML += `</div></div>`;
        });
        
        // --- PART 2: Render selected ADD-ONS ---
        const selectedAddons = [];
        if (this.stepContentEl) {
            const checkedToggles = this.stepContentEl.querySelectorAll('.multi-step-addons__card-wrapper .addon-toggle-checkbox:checked');
            checkedToggles.forEach(toggle => {
                const cardWrapper = toggle.closest('.multi-step-addons__card-wrapper');
                const productId = parseInt(cardWrapper.dataset.productId);
                const productData = this.findProductById(productId);
                if (!productData) return;

                const currentlySelectedOptions = Array.from(cardWrapper.querySelectorAll('input.variant-radio-input:checked')).map(radio => radio.value);
                const selectedVariant = productData.variants.find(variant =>
                    currentlySelectedOptions.every((opt, index) => variant.options[index] === opt)
                );

                if (selectedVariant) {
                    selectedAddons.push({
                        productTitle: productData.title,
                        variantTitle: selectedVariant.title,
                        price: selectedVariant.price
                    });
                }
            });
        }

        if (selectedAddons.length > 0) {
            // Add the main "Add Accessories" header once
            selectionsHTML += `<div class="overview-item">`;
            const addonsStepConfig = this.config.steps[this.currentStepIndex];
            const addonsLabel = addonsStepConfig?.summary_label || 'Accessories';
            selectionsHTML += `<span class="overview-item__label">${addonsLabel}</span>`;

            // Add each selected addon as a sub-item
            selectedAddons.forEach(addon => {
                selectionsHTML += `<div class="overview-item overview-item--sub-item">`;
                selectionsHTML += `<span class="overview-item__label"></span>`; // Empty label for sub-items
                selectionsHTML += `<div class="overview-item__details">`;
                selectionsHTML += `<span class="overview-item__value">${addon.productTitle} (${addon.variantTitle})</span>`;
                // if (addon.price > 0) {
                //     selectionsHTML += `<span class="overview-item__price">${this.formatMoney(addon.price)}</span>`;
                // }
                selectionsHTML += `</div></div>`;
            });
        }

        // --- PART 3: Render Prescription Addons ---
        const matchingPrescriptionAddons = this.getMatchingAddons();
        if (matchingPrescriptionAddons.length > 0) {
            let totalAddonPrice = 0;
            matchingPrescriptionAddons.forEach(rule => {
                 const addonData = this.config.addonDataMap[rule.variantId];
                 if (addonData) totalAddonPrice += addonData.price;
            });

            selectionsHTML += `<div class="overview-item">`;
            selectionsHTML += `<span class="overview-item__label">Prescription</span>`;
            selectionsHTML += `<div class="overview-item__details">`;
            selectionsHTML += `<span class="overview-item__value">Lens</span>`;
            // if (totalAddonPrice > 0) {
            //     selectionsHTML += `<span class="overview-item__price">${this.formatMoney(totalAddonPrice)}</span>`;
            // } else {
            //     selectionsHTML += `<span class="overview-item__price overview-item__price--free">${this.translations.included || 'FREE'}</span>`;
            // }
            selectionsHTML += `</div></div>`;
        }
        
        selectionsContainer.innerHTML = selectionsHTML;
      }

      restoreSelectionUI(stepIndex) {
         const selection = this.selections[stepIndex];

         if (!selection || selection.skipped) {
              if (!selection && this.stepContentEl) {
                 this.stepContentEl.querySelectorAll?.('.prescription-grid__select.is-active')
                     .forEach(el => el.classList.remove('is-active'));
              }
              this.updateNavigationButtonsState();
              return;
         }

         if (selection.type === 'input') {
             // --- Prescription Grid Restoration ---
             const selects = this.stepContentEl?.querySelectorAll('.prescription-grid__select');
             selects?.forEach(select => {
                 const fieldName = select.name;
                 if (selection.values && selection.values.hasOwnProperty(fieldName)) {
                     const storedValue = selection.values[fieldName];
                     select.value = storedValue;
                     select.classList.add('is-active');
                 } else {
                     select.value = select.options[0]?.value || '';
                     select.classList.remove('is-active');
                 }
             });

             // --- Prescription Extras Radio Restoration (FIXED Placeholders) ---
             if (selection.id) { // Check if the stored selection has an ID (could be an extra radio)
                 // Construct the query selector to find the radio by value or ID in data-option-data
                 const radioSelector = `.prescription-extras input[type="radio"][value="${selection.id}"], .prescription-extras input[type="radio"][data-option-data*='"id":${JSON.stringify(selection.id)}']`;
                 const inputRadio = this.stepContentEl?.querySelector(radioSelector);
                 if (inputRadio) {
                     inputRadio.checked = true;
                 } else {
                     console.warn("RestoreUI: Could not find prescription extra radio to check for ID:", selection.id);
                 }
             } else {
                 // If the stored selection was only grid values (no ID), ensure extra radios are unchecked
                 const extraRadios = this.stepContentEl?.querySelectorAll('.prescription-extras input[type="radio"]');
                 extraRadios?.forEach(radio => {
                     if (radio.checked) {
                         radio.checked = false;
                     }
                 });
             }
             // --- END FIX ---

         } else if (selection.id || selection.option_label) {
             // --- Standard Radio Button Restoration (FIXED Placeholders) ---
             let input = null; // Initialize input
             if (selection.id) {
                  // Construct the query selector to find the radio by value or ID in data-option-data
                  // Make sure it *doesn't* accidentally select radios inside .prescription-extras
                  const radioSelector = `input[type="radio"][value="${selection.id}"]:not(.prescription-extras input), input[type="radio"][data-option-data*='"id":${JSON.stringify(selection.id)}']:not(.prescription-extras input)`;
                  input = this.stepContentEl?.querySelector(radioSelector);
             }
             // If not found by ID, try finding by label in data attribute
             if (!input && selection.option_label) {
                  const allRadios = this.stepContentEl?.querySelectorAll('input[type="radio"][data-option-data]:not(.prescription-extras input)');
                  input = Array.from(allRadios || []).find(radio => {
                      try {
                          const data = JSON.parse(radio.dataset.optionData);
                          return data.option_label === selection.option_label;
                      } catch { return false; }
                  });
             }

             // Check the input if found
             if (input) {
                  input.checked = true;
             } else {
                  console.warn(`RestoreUI: Could not find standard step radio for step ${stepIndex} with data:`, selection);
             }
             // --- END FIX ---
         }

         this.updateNavigationButtonsState();
      }

      clearSubsequentSelections(currentStepIndex) {
          Object.keys(this.selections).forEach(key => {
              const index = parseInt(key, 10);
              if (index > currentStepIndex) {
                  delete this.selections[index];
                  // Also potentially clear related additionalItems or properties
              }
          });
          // Need to recalculate subtotal after clearing
          this.recalculateSubtotal();
          this.updateSubtotalDisplay();
      }

      findInputStepIndex() {
          return this.config.steps.findIndex(step => step.step_type === 'input');
      }

      clearPrescriptionProperties(calledFromClearSubsequent = false) {
         console.warn(`%c>>> clearPrescriptionProperties called! <<<`, 'color: red; font-weight: bold;', `(Called from clearSubsequent: ${calledFromClearSubsequent})`);
    
        let propsCleared = false;
         Object.keys(this.lineItemProperties).forEach(key => {
             // --- MODIFIED: Updated Regex (no leading underscore) ---
             if (key.match(/^_(SPH|CYL|AXIS|ADD|PD)_(OD|OS)$/i)) {
             // --- END MODIFICATION ---
                 console.warn(`   Deleting property: ${key}`);
                 delete this.lineItemProperties[key];
                 propsCleared = true;
             }
         });
    
         let selectionCleared = false; // Flag specifically for selections
         if (!calledFromClearSubsequent) {
           const inputStepIndex = this.findInputStepIndex();
           // Check if index is valid and selections state exists for that index
           if (inputStepIndex !== -1 && this.selections && this.selections[inputStepIndex]) {
             console.warn(`   Clearing prescription values/selection from selections state for step: ${inputStepIndex}`);
             // Check the type of selection stored
             if (this.selections[inputStepIndex].type === 'input') {
                 // If it's the input grid type, just clear the entered values
                 this.selections[inputStepIndex].values = {};
                 // Optionally reset a completion flag if you use one
                 // this.selections[inputStepIndex].completed = false;
                 selectionCleared = true;
             } else {
                 // If it's not type 'input' (e.g., an extra radio was selected), remove the whole entry
                 delete this.selections[inputStepIndex];
                 selectionCleared = true;
             }
           }
         }
    
         if (propsCleared || selectionCleared) {
            // Use || {} as fallback in case lineItemProperties itself was somehow nulled
            console.warn(`   Cleared prescription properties and/or selections state. Current lineItemProperties:`, JSON.parse(JSON.stringify(this.lineItemProperties || {})));
            this.updateOverview(); // Update overview as prescription state changed
         } else {
           console.warn(`   clearPrescriptionProperties called, but no relevant properties or selections found/cleared.`);
         }
      }

      processPrescriptionAddons() {
        console.log('%c[Addons] Processing prescription-based addons...', 'color: blue; font-weight: bold;');
        const addonsToApply = this.getMatchingAddons();

        addonsToApply.forEach(rule => {
            console.log(`%c[Addons] Rule MATCHED: Adding variant ${rule.variantId}`, 'color: green;');
            this.additionalItems.push({
                id: rule.variantId,
                quantity: 1
            });
        });
      }   

      async handleAddToCart(event) {
        if (this.navigationContainer.querySelector('[data-action="add"]')?.disabled) return;
        this.setLoading(true);
    
        const mainItemId = this.config.initialVariantId;
        const finalItems = []; // We will push add-ons here, then unshift the main item at the end
        const finalProperties = {}; // For the Main Item
        
        // 1. Setup Price Calculation (Base Price)
        // We will add to this for Standard Options and Prescription Rules, but NOT Manual Add-ons
        let calculatedFinalPrice = this.basePrice; 
        
        // 2. Setup Shared Properties for Non-Main Items
        const addonProperties = {
            '_hide': 'yes',
            '_linked_product': mainItemId
        };
        
        // 3. Setup Array to capture Prescription Add-on Names
        const prescriptionNames = [];
    
        // --- PROCESS A: Standard SELECTION Steps ---
        Object.values(this.selections).forEach(selection => {
          if (selection && !selection.skipped && selection.type !== 'input') {
            const stepConfig = this.config.steps.find(step => step.options && step.options.some(opt => opt.option_label === selection.option_label));
            
            // Add Property to Main Item (e.g. Frame Color: Blue)
            if (stepConfig) finalProperties[`_${stepConfig.summary_label || stepConfig.step_title}`] = selection.option_label;
            
            // Calculate Price (Include in Final Price)
            const price = selection.price_override ?? selection.product_ref?.price ?? 0;
            calculatedFinalPrice += price;

            // Add Linked Product (if this option is a product)
            if (selection.product_ref?.id) {
                finalItems.push({ 
                    id: selection.product_ref.id, 
                    quantity: 1,
                    properties: { ...addonProperties } // <--- Add hide/linked logic
                });
            }
          }
        });
    
        // --- PROCESS B: Prescription Inputs (Values) ---
        Object.keys(this.lineItemProperties).forEach(key => {
            if (key.match(/^_(SPH|CYL|AXIS|ADD|PD)_(OD|OS)$/i)) finalProperties[key] = this.lineItemProperties[key];
        });
        
        // --- PROCESS C: Prescription Rules (Logic-based Add-ons) ---
        const matchingPrescriptionAddons = this.getMatchingAddons();
        matchingPrescriptionAddons.forEach(rule => {
             const variantId = parseInt(rule.variantId);
             
             // Look up data from the Liquid JSON map
             const addonData = this.config.addonDataMap[rule.variantId];

             if (addonData) {
                 // 1. Add Price
                 calculatedFinalPrice += addonData.price;

                 // 2. Add Title (From Liquid)
                 if (addonData.title) {
                     prescriptionNames.push(addonData.title);
                 }
             }

             // 3. Add Item
             finalItems.push({ 
                 id: variantId, 
                 quantity: 1,
                 properties: { ...addonProperties }
             });
        });
    
        // --- PROCESS D: Manual ADD-ONS (Accessories UI) ---
        // Note: We DO NOT add these prices to 'calculatedFinalPrice' per instructions
        if (this.stepContentEl) {
            const allToggles = this.stepContentEl.querySelectorAll('.multi-step-addons__card-wrapper .addon-toggle-checkbox:checked');
            allToggles.forEach(toggle => {
                const cardWrapper = toggle.closest('.multi-step-addons__card-wrapper');
                const productId = parseInt(cardWrapper.dataset.productId);
                const productData = this.findProductById(productId);
                if (!productData) return;

                const currentlySelectedOptions = Array.from(cardWrapper.querySelectorAll('input.variant-radio-input:checked')).map(radio => radio.value);
                const variantToAdd = productData.variants.find(variant =>
                    currentlySelectedOptions.every((opt, index) => variant.options[index] === opt)
                );
                
                if (variantToAdd) {
                    finalItems.push({ 
                        id: variantToAdd.id, 
                        quantity: 1,
                    });
                }
            });
        }
    
        // --- FINALIZING MAIN ITEM ---
        // 1. Get the value with exactly 2 decimals (e.g., "4560.00")
        const decimalValue = (calculatedFinalPrice / 100).toFixed(2);

        // 2. Add comma separators for thousands (e.g., "4,560.00")
        const parts = decimalValue.split(".");
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        const finalString = parts.join(".");

        // 3. Hardcode the currency symbol + the formatted number
        finalProperties['_finalPrice'] = '₱' + finalString;

        // Add "Prescription" property (Names of matching rules)
        if (prescriptionNames.length > 0) {
            // Join with comma if multiple, e.g. "High Index 1.67, Blue Light Filter"
            finalProperties['Prescription'] = prescriptionNames.join(', ');
        }

        // Add Main Item to the very beginning of the array
        finalItems.unshift({ id: mainItemId, quantity: 1, properties: finalProperties });
    
        console.log('%c[Add to Cart] Final items payload:', 'color: blue; font-weight: bold;', finalItems);
        
        // ... The rest of the function (AJAX call) remains the same ... 
        
        const formData = { items: finalItems };
    
        // Prepare sections to request update for
        let sectionsToRequest = [];
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
           sectionsToRequest = this.cart.getSectionsToRender().map((section) => section.id);
           formData.sections = sectionsToRequest;
           formData.sections_url = window.location.pathname;
           this.cart.setActiveElement?.(document.activeElement);
        } else {
           console.warn("MultiStepModal: Cart element or getSectionsToRender not found. Cannot request section updates.");
           formData.sections = ['cart-drawer', 'cart-icon-bubble']; 
           formData.sections_url = window.location.pathname;
        }
        
        if (!sectionsToRequest.includes('cart-icon-bubble')) formData.sections.push('cart-icon-bubble');
        if (!sectionsToRequest.includes('cart-drawer')) formData.sections.push('cart-drawer');
        
        try {
          const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { headers: {} };
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.headers['Accept'] = 'application/json';
          config.body = JSON.stringify(formData);
    
          const response = await fetch(this.config.routes.cart_add_url, {
            method: 'POST',
            headers: config.headers,
            body: config.body
          });
    
          const responseJson = await response.json();
    
          if (response.status >= 300) {
            this.error = true;
            let message = responseJson.description || responseJson.message || JSON.stringify(responseJson);
            throw new Error(message);
          }
    
          if (!this.error) {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
                   publish(PUB_SUB_EVENTS.cartUpdate, {
                      source: 'multi-step-modal',
                      productVariantId: mainItemId,
                      cartData: responseJson
                   });
              }

              if (this.cart && typeof this.cart.renderContents === 'function') {
                    this.cart.renderContents(responseJson);
              }
    
              if (responseJson.sections && responseJson.sections['cart-icon-bubble']) {
                  try {
                      const bubbleHTMLString = responseJson.sections['cart-icon-bubble'];
                      const parser = new DOMParser();
                      const parsedHTML = parser.parseFromString(bubbleHTMLString, 'text/html');
                      const sectionDiv = parsedHTML.body.firstElementChild;
                      if (sectionDiv) {
                         const newBubbleContent = sectionDiv.innerHTML; 
                         const targetBubbleElement = document.getElementById('cart-icon-bubble');
                         if (targetBubbleElement) targetBubbleElement.innerHTML = newBubbleContent;
                      }
                  } catch (e) {
                      console.error("MultiStepModal: Error parsing bubble HTML.", e);
                  }
              }
    
              if (this.cart && this.cart.classList.contains('is-empty')) {
                 this.cart.classList.remove('is-empty');
                  const emptyDiv = this.cart.querySelector('.drawer__inner-empty');
                  if (emptyDiv) emptyDiv.style.display = 'none';
              }
    
             setTimeout(() => {
                 const checkoutButton = document.getElementById('CartDrawer-Checkout');
                 if (checkoutButton) checkoutButton.removeAttribute('disabled');
             }, 100); 
    
              if (this.cart && typeof this.cart.open === 'function') {
                  setTimeout(() => { this.cart.open(); }, 150);
              }
    
              this.modal.hide();
          }
    
        } catch (error) {
           this.error = true;
           console.error("Error adding items to cart:", error);
           let errorMessage = error.message || this.translations?.cartError || "Could not add item to cart.";
            try {
              const parsedError = JSON.parse(error.message || error);
              if (parsedError.errors) errorMessage = parsedError.errors;
            } catch (e) {}
            
            const newErrorEl = document.createElement('div');
            newErrorEl.className = 'multi-step-modal__error form__message form__message--error';
            newErrorEl.textContent = errorMessage;
            this.navigationContainer.parentNode.insertBefore(newErrorEl, this.navigationContainer);
    
        } finally {
          this.setLoading(false);
          const finalAddButton = this.navigationContainer.querySelector('[data-action="add"]');
          if (!this.error && finalAddButton) {
            finalAddButton.disabled = false;
          }
        }
      }

      setLoading(isLoading) {
         this.loadingSpinner?.classList.toggle('hidden', !isLoading); // Use the spinner variable
         const buttons = this.navigationContainer?.querySelectorAll('button'); // Add safety check for container
         buttons?.forEach(button => button.disabled = isLoading);
      }

      getCartSectionsToRender() {
         return this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [];
      }

      updateCartUI(cartData) {
         // Reuse logic from product-form.js or cart components
         const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
         if (cart) {
            // CartPerformance.measure("add:paint-updated-sections", () => { // Optional perf tracking
                 cart.renderContents(cartData);
            // });
         }
      }

      checkPrescriptionCompletion() {
        if (!this.stepContentEl) {
            console.error("checkPrescriptionCompletion: stepContentEl not found!");
            return false;
        }

        const gridContainer = this.stepContentEl.querySelector('.prescription-grid');
        if (!gridContainer) {
            return false;
        }

        const selects = gridContainer.querySelectorAll('select.prescription-grid__select');
        if (selects.length === 0) {
            return true; // No inputs to validate
        }

        // Use Array.every() for a cleaner check. It stops as soon as one is invalid.
        const allValid = Array.from(selects).every(select => {
            // A select is considered "complete" if:
            // 1. It has been interacted with (has 'is-active' class)
            // OR
            // 2. It is hidden from the user (offsetParent is null when display: none)
            const isComplete = select.classList.contains('is-active') || select.offsetParent === null;

            return isComplete;
        });

        return allValid;
      }

      getMatchingAddons() {
        console.log('%c[Debug] Running getMatchingAddons...', 'color: #9932CC; font-weight: bold;');

        // 1. Check the labels it found
        const previousLabels = this.getPreviousSelectionLabels();
        console.log('[Debug] Input Labels:', previousLabels);

        // 2. Check the raw prescription data it's working with
        console.log('[Debug] Input Prescription Properties:', JSON.parse(JSON.stringify(this.lineItemProperties)));

        const matchingRules = [];
        if (!this.lineItemProperties || Object.keys(this.lineItemProperties).length === 0) {
            console.warn('[Debug] No prescription properties found. Exiting.');
            return [];
        }

        // 3. Check the parsed numeric values
        const sphOD = parseFloat(this.lineItemProperties._SPH_OD);
        const sphOS = parseFloat(this.lineItemProperties._SPH_OS);
        const cylOD = parseFloat(this.lineItemProperties._CYL_OD);
        const cylOS = parseFloat(this.lineItemProperties._CYL_OS);
        console.log('[Debug] Parsed Prescription Values:', { sphOD, sphOS, cylOD, cylOS });

        this.PRESCRIPTION_ADDON_RULES.forEach(rule => {
          // 4. Create a detailed log for each rule evaluation
          console.groupCollapsed(`[Debug] Evaluating Rule: {vision: "${rule.visionType}", param: "${rule.parameter}"}`);

          const visionMatch = previousLabels.has(rule.visionType);
          console.log(`Vision type matches? ${visionMatch}`);

          if (visionMatch) {
            let value1, value2;
            if (rule.parameter === 'SPH') { [value1, value2] = [sphOD, sphOS]; }
            if (rule.parameter === 'CYL') { [value1, value2] = [cylOD, cylOS]; }

            console.log(`Checking against values: [${value1}, ${value2}]`);

            const result1 = rule.condition(value1);
            const result2 = rule.condition(value2);
            console.log(`Condition result for Eye 1 (${value1}): ${result1}`);
            console.log(`Condition result for Eye 2 (${value2}): ${result2}`);

            if (result1 || result2) {
              console.log('%cDecision: MATCHED', 'color: green;');
              if (!matchingRules.some(r => r.variantId === rule.variantId)) {
                  matchingRules.push(rule);
              }
            } else {
              console.log('%cDecision: NOT MATCHED', 'color: red;');
            }
          }
          console.groupEnd();
        });

        console.log('%c[Debug] Final Matched Rules:', 'color: #9932CC; font-weight: bold;', matchingRules);
        return matchingRules;
      }

      formatMoney(cents) {
        if (typeof cents !== 'number') return '';
        // Determine if decimals should be shown based on config
        const showDecimals = this.config?.showDecimals === true; // Default to false if undefined/null
        const decimalPlaces = showDecimals ? 2 : 0;
    
        try {
            // Use Shopify's money format passed in config
            const format = this.config?.moneyFormat || '${{amount}}'; // Example: "₱{{amount}}"
            let value = (cents / 100.0).toFixed(decimalPlaces);
    
            // More robust placeholder replacement needed for various Shopify formats
            // This is a simplified version, assuming common formats like {{amount}} or {{amount_no_decimals}}
            let formattedValue = format;
            if (!showDecimals && format.includes('{{amount_no_decimals}}')) {
                 // Format exists specifically for no decimals
                 formattedValue = format.replace('{{amount_no_decimals}}', value);
            } else if (!showDecimals && format.includes('{{amount}}')) {
                 // Standard format, but we need to remove decimals manually if needed
                 formattedValue = format.replace('{{amount}}', value);
                 formattedValue = formattedValue.replace(/[.,]00$/, ''); // Remove trailing .00 or ,00
            } else if (format.includes('{{amount_with_comma_separator}}')) {
                 value = value.replace('.', ','); // Basic EU style
                 formattedValue = format.replace('{{amount_with_comma_separator}}', value);
                 if (!showDecimals) formattedValue = formattedValue.replace(/,00$/, '');
            } else {
                 // Default replace {{amount}} or handle other formats as needed
                 formattedValue = format.replace('{{amount}}', value);
                  if (!showDecimals) formattedValue = formattedValue.replace(/[.,]00$/, '');
            }
    
            return formattedValue;
    
        } catch (e) {
           console.warn("Money formatting error", e);
           // Fallback formatting respecting decimals
           const fallbackValue = (cents / 100.0).toFixed(decimalPlaces);
           // Basic fallback assuming $ prefix if format fails completely
           return `$${fallbackValue}`;
        }
      }   

      // Helper to find a title for the "Prescription" property on the main item
      findTitleByVariantId(targetId) {
        if (!this.config || !this.config.steps) return 'Add-on';
        
        // Loop through all steps to find where this variant might be defined in addon_products
        for (const step of this.config.steps) {
            if (step.addon_products && Array.isArray(step.addon_products)) {
                for (const product of step.addon_products) {
                    const variant = product.variants.find(v => v.id == targetId);
                    if (variant) {
                        // Return "Product Name" (or "Product Name - Variant Name" if useful)
                        return variant.title === 'Default Title' ? product.title : `${product.title} (${variant.title})`;
                    }
                }
            }
        }
        return 'Lens Upgrade'; // Fallback if name not found in config
      }

      getNextStepIndexToShow(currentIndex) {
        let potentialNextIndex = currentIndex + 1;

        while (potentialNextIndex < this.config.steps.length) {
            const nextStepData = this.config.steps[potentialNextIndex];

            // Check if this step should be rendered based on the now-correct previous selection
            if (!this.shouldRenderStep(potentialNextIndex)) {
                // It shouldn't render, so mark it as auto-skipped and check the next one
                if (!this.selections[potentialNextIndex] || !this.selections[potentialNextIndex].skipped) {
                    this.selections[potentialNextIndex] = {
                        skipped: true,
                        option_label: 'Skipped (Auto)'
                    };
                }
                potentialNextIndex++;
                continue;
            }

            // Special check for skipping the prescription step
            const isPrescriptionInputStep =
                nextStepData.step_type === 'input' &&
                nextStepData.step_title?.toLowerCase().includes('prescription');

            if (isPrescriptionInputStep && this.shouldSkipPrescriptionStep(potentialNextIndex)) {
                // Mark prescription as auto-skipped and check the next one
                if (!this.selections[potentialNextIndex] || !this.selections[potentialNextIndex].skipped) {
                    this.selections[potentialNextIndex] = {
                        skipped: true,
                        option_label: 'Skipped (Auto)'
                    };
                }
                potentialNextIndex++;
                continue;
            }

            // If we get here, this is the correct step to show.
            return potentialNextIndex;
        }

        // The loop finished, meaning we've run out of steps.
        return potentialNextIndex;
      }

      closeAllTooltips(exceptThisOne = null) {
          this.stepContentEl.querySelectorAll('.tooltip-content').forEach(tip => {
              if (tip !== exceptThisOne && !tip.hidden) {
                  tip.hidden = true;
                  const icon = tip.previousElementSibling; // Assumes icon is previous sibling
                  if (icon && icon.classList.contains('tooltip-icon')) {
                     icon.setAttribute('aria-expanded', 'false');
                  }
              }
          });
      }

      clearErrorMessages() {
        const errorElement = this.modal?.querySelector('.multi-step-modal__error');
        if (errorElement) {
            errorElement.remove();
        }
        // Reset the internal error flag as well
        this.error = false;
      }

      updateAxisCylDependency(changedElement) {
        const col = changedElement.dataset.col;
        const row = changedElement.dataset.row; // 'OD' or 'OS'

        // Exit if the changed element is not CYL or AXIS
        if (col !== 'CYL' && col !== 'AXIS') {
            return;
        }

        const cylSelect = this.stepContentEl.querySelector(`select[data-row="${row}"][data-col="CYL"]`);
        const axisSelect = this.stepContentEl.querySelector(`select[data-row="${row}"][data-col="AXIS"]`);

        if (!cylSelect || !axisSelect) return;

        // Rule 1: CYL was changed, so update AXIS.
        if (col === 'CYL') {
            const cylValue = changedElement.value;
            const cylIsNumeric = !isNaN(parseFloat(cylValue));
            const axisNoneOption = axisSelect.querySelector('option[value="None"]');

            if (axisNoneOption) {
            axisNoneOption.hidden = cylIsNumeric;
            }

            // If the currently selected AXIS option is now hidden, reset AXIS to the first visible option.
            if (axisSelect.options[axisSelect.selectedIndex]?.hidden) {
            // Find the first option that is NOT hidden
            const firstVisibleOption = Array.from(axisSelect.options).find(opt => !opt.hidden);
            if (firstVisibleOption) {
                axisSelect.value = firstVisibleOption.value;
                // Manually trigger a change event on the AXIS select to ensure its state is saved
                axisSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
            }
        }

        // Rule 2: AXIS was changed, so update CYL.
        if (col === 'AXIS') {
            const axisIsNone = changedElement.value.toLowerCase() === 'none';

            // Loop through all CYL options and hide/show them
            Array.from(cylSelect.options).forEach(option => {
            const cylIsNumeric = !isNaN(parseFloat(option.value));
            if (cylIsNumeric) {
                option.hidden = axisIsNone;
            }
            });

            // If the currently selected CYL option is now hidden, reset CYL to "None".
            if (cylSelect.options[cylSelect.selectedIndex]?.hidden) {
            cylSelect.value = 'None';
            // Manually trigger a change event on the CYL select to ensure its state is saved
            cylSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
      }

      renderAddonsStep(stepData) {
        const fragment = document.createDocumentFragment();
        const addonsContainer = document.createElement('div');
        addonsContainer.className = 'multi-step-addons__container';

        const sourceContainer = document.querySelector(`#MultiStepAddonsSource-${this.sectionId}`);
        if (!sourceContainer) return;

        const productIds = (stepData.addon_products || []).map(p => p.id);

        productIds.forEach(productId => {
          const sourceCardWrapper = sourceContainer.querySelector(`.multi-step-addons__card-wrapper[data-product-id="${productId}"]`);
          if (sourceCardWrapper) {
            const newCard = sourceCardWrapper.cloneNode(true);

            const checkedRadios = newCard.querySelectorAll('input.variant-radio-input:checked');
            checkedRadios.forEach(radio => {
            if (!radio.disabled) {
                radio.classList.add('is-selected');
            }
            });

            // --- FIX 1: Make ALL cloned IDs unique ---
            const toggle = newCard.querySelector('.addon-toggle-checkbox');
            const toggleLabel = newCard.querySelector('.addon-toggle-label');
            if (toggle && toggleLabel) {
                const newToggleId = `addon-toggle-clone-${productId}`;
                toggle.id = newToggleId;
                toggleLabel.htmlFor = newToggleId;
            }

            const inputs = newCard.querySelectorAll('.variant-radio-input');
            inputs.forEach(input => {
                const oldId = input.id;
                const newId = `clone-${oldId}`;
                input.id = newId;
                const label = newCard.querySelector(`label[for="${oldId}"]`);
                if (label) label.htmlFor = newId;
            });
            // --- END FIX 1 ---

            const isAlreadySelected = this.additionalItems.some(item => {
                const variant = this.findVariantById(productId, item.id);
                return !!variant;
            });
            if (toggle) toggle.checked = isAlreadySelected;

            // --- FIX 2: Disable toggle if all variants are disabled ---
            // 1. Go through all the inputs inside .variant-picker__values
            const variantPickerInputs = newCard.querySelectorAll('.variant-picker__values input');
            
            if (variantPickerInputs.length > 0) {
                // Check if ALL of them have the disabled attribute
                const allDisabled = Array.from(variantPickerInputs).every(input => input.disabled);

                // 2. If all are disabled, disable the closest (current) toggle
                if (allDisabled && toggle) {
                toggle.disabled = true;
                toggle.checked = false; 
                
                // Optional: Add a class to the label for visual styling if needed
                if (toggleLabel) toggleLabel.classList.add('is-disabled'); 

                const toggleParent = toggle.closest('.multi-step-addons__card-wrapper');
                    if (toggleParent) {
                        toggleParent.classList.add('is-disabled');
                    }
                }
            }

            addonsContainer.appendChild(newCard);

            const firstRadio = newCard.querySelector('input.variant-radio-input');
            if (firstRadio) this.handleAddonChange(firstRadio);
          }
        });

        fragment.appendChild(addonsContainer);
        this.stepContentEl.innerHTML = '';
        this.stepContentEl.appendChild(fragment);
      }

      renderAddonPrice(container, variant) {
        const price = this.formatMoney(variant.price);
        const compareAtPrice = variant.compare_at_price ? this.formatMoney(variant.compare_at_price) : null;
        let priceHTML = '';

        if (compareAtPrice && variant.price < variant.compare_at_price) {
            priceHTML = `
            <span class="price-item price-item--sale">${price}</span>
            <s class="price-item price-item--regular">${compareAtPrice}</s>
            `;
        } else {
            priceHTML = `<span class="price-item price-item--regular">${price}</span>`;
        }
        container.innerHTML = priceHTML;
      }

      findProductById(productId) {
        const stepData = this.config.steps[this.currentStepIndex];
        if (!stepData || !stepData.addon_products) return null;
        return stepData.addon_products.find(p => p.id == productId);
      }

      // Helper to find a specific variant object within a product
      findVariantById(productId, variantId) {
        const product = this.findProductById(productId);
        if (!product) return null;
        return product.variants.find(v => v.id == variantId);
      }

      handlePrescriptionChange(selectElement) {
        console.log(`[State] Change detected in ${selectElement.name}. Updating state now.`);
        // Update state immediately for live calculations
        const propertyKey = `_${selectElement.name}`;
        this.lineItemProperties[propertyKey] = selectElement.value;
        const stepIndex = this.currentStepIndex;
        if (!this.selections[stepIndex]) this.selections[stepIndex] = { type: 'input', values: {} };
        this.selections[stepIndex].values[selectElement.name] = selectElement.value;

        // Run live updates
        this.updateAxisCylDependency(selectElement);
        this.recalculateSubtotal();
        this.updateSubtotalDisplay();
        this.updateOverview();
      }

      handleAddonChange(target) {
        const cardWrapper = target.closest('.multi-step-addons__card-wrapper');
        if (!cardWrapper) return;

        console.group('%c[Addons Change Event]', 'color: #007bff; font-weight: bold;');
        console.log('Event triggered by:', target);

        

        // Update Variant UI if a radio was changed
        if (target.matches('.variant-radio-input')) {
            const productId = parseInt(cardWrapper.dataset.productId);
            const productData = this.findProductById(productId);
            if (!productData) { console.groupEnd(); return; }

            const radiosInGroup = cardWrapper.querySelectorAll(`input[name="${target.name}"]`);
            radiosInGroup.forEach(radio => radio.classList.remove('is-selected'));
            // Then, add it back to the one that was just clicked
            if (!target.disabled) {
                target.classList.add('is-selected');
            }
            
            const currentlySelectedOptions = Array.from(cardWrapper.querySelectorAll('input.variant-radio-input:checked')).map(radio => radio.value);
            const selectedVariant = productData.variants.find(variant =>
                currentlySelectedOptions.every((opt, index) => variant.options[index] === opt)
            );

            if (selectedVariant) {
                console.log('UI Update: Found matching variant:', selectedVariant);
                const variantTitleEl = cardWrapper.querySelector('[data-variant-title]');
                const priceContainer = cardWrapper.querySelector('.price');
                const imageContainer = cardWrapper.querySelector('.card__media img')
                if (variantTitleEl) variantTitleEl.textContent = selectedVariant.title;
                if (priceContainer) this.renderAddonPrice(priceContainer, selectedVariant);
                if (imageContainer && selectedVariant.featured_image) {
                    const newImage = new Image();
                    newImage.onload = () => {
                        imageContainer.src = selectedVariant.featured_image.src;
                        imageContainer.alt = selectedVariant.featured_image.alt;
                    };
                    newImage.src = selectedVariant.featured_image.src;
                }
            }
        }

        // Always recalculate everything to keep UI in sync
        this.recalculateSubtotal();
        this.updateSubtotalDisplay();
        this.updateOverview(); // <--- THIS FIXES THE OVERVIEW SYNC
        this.updateNavigationButtonsState();

        console.groupEnd();
      }

      initializeButtonState() {
        // Find the button specifically associated with this modal's section
        // We look up to the container, then down to the button to ensure we get the right one
        const container = this.closest('#custom_steps_container');
        if (!container) return;

        const button = container.querySelector('button.custom-product-cta');

        if (button) {
            // If we have valid config data, enable the button
            if (this.config) {
                button.removeAttribute('disabled');
            } else {
                // OPTIONAL: If config failed to load, keep it disabled to prevent errors
                console.warn('MultiStepModal: Config missing, keeping button disabled.');
                button.setAttribute('disabled', 'true');
            }
        }
      }

    }
  );
} 

// Make sure ModalDialog is loaded before this script
// Ensure global functions like getFocusableElements, trapFocus are available